import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Check, Theater } from 'lucide-react';

const EditableScenicTab = ({ event, onUpdate }) => {
  const [scenicStaging, setScenicStaging] = useState(event?.scenic_staging || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Track pending changes and backup timer
  const pendingChanges = useRef({});
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Only sync with parent on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      setScenicStaging(event?.scenic_staging || {});
      isInitialMount.current = false;
    }
  }, [event?.scenic_staging]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
    };
  }, []);

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    
    try {
      await onUpdate({ scenic_staging: changesToSave });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      pendingChanges.current = { ...changesToSave, ...pendingChanges.current };
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change
  const queueChange = useCallback((newScenicStaging) => {
    pendingChanges.current = newScenicStaging;
    
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    
    backupTimeoutRef.current = setTimeout(() => {
      doSave();
    }, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  const handleFieldChange = (field, value) => {
    const newScenicStaging = { ...scenicStaging, [field]: value };
    setScenicStaging(newScenicStaging);
    queueChange(newScenicStaging);
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Theater className="h-5 w-5 text-pink-400" />
            Scenic / Staging
          </CardTitle>
          <div className="flex items-center h-6">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Scenic</Label>
            <Textarea
              value={scenicStaging.scenic || ''}
              onChange={(e) => handleFieldChange('scenic', e.target.value)}
              onBlur={saveNow}
              placeholder="Scenic elements, backdrops, set pieces..."
              rows={8}
              className="bg-background/50"
              data-testid="scenic-input"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Staging</Label>
            <Textarea
              value={scenicStaging.staging || ''}
              onChange={(e) => handleFieldChange('staging', e.target.value)}
              onBlur={saveNow}
              placeholder="Stage setup, risers, platforms..."
              rows={8}
              className="bg-background/50"
              data-testid="staging-input"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EditableScenicTab;
